import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import "./ClientPlatformAnalytics.css";
import "./PublicPlatformReport.css";
import "./PublicFundingRequest.css";
import CphereHeaderLogo from "../components/CphereHeaderLogo";
import CphereBrandFooter from "../components/CphereBrandFooter";
import { resolveFundingAssetVisual } from "../utils/fundingAssetVisual";
import {
  FUNDING_STATUS_POLL_INTERVAL_MS,
  QUOTE_AWAITING_QR_COPY,
  QUOTE_TICK_INTERVAL_MS,
  QUOTE_URGENT_SECONDS,
  buildPublicFundingBase,
  formatAssetAmount,
  formatCountdown,
  formatIssuedAt,
  formatNetworkLine,
  formatPayWithLine,
  formatQuoteAmount,
  formatUsd,
  isNonZeroAmount,
  isStablecoinParQuote,
  isTerminalFundingStatus,
  isUsdDenomination,
  mergeFundingStatus,
  quoteDeadlineMs,
  quoteIsUsable,
  quoteProvenanceLine,
  quoteSecondsLeft,
  quoteUnavailableCopy,
  quoteUnitLine,
  sanitizePublicFundingRequest,
  statusTone,
} from "./publicFundingRequestModel";

/**
 * Public compute funding request — /fund/:fundingToken
 *
 * A no-login payment page for one campaign's compute funding request. Modelled on the
 * public Platform Analytics report and it keeps the same three properties:
 *
 *  1. SCOPE COMES FROM THE TOKEN. The URL segment is the entire credential.
 *     The page never sends an advertiser, campaign or request id, never sends
 *     an Authorization header, and never sends cookies.
 *
 *  2. EVERYTHING SHOWN IS SERVER COPY. Amounts, the reference label, the
 *     instructions, the fee lines and the safety copy are rendered verbatim
 *     from the response. The page formats numbers; it does not author claims.
 *     The response is reduced to the public contract's fields on arrival, so
 *     nothing outside that contract can reach the DOM.
 *
 *  3. NO APPLICATION CHROME. No navigation, no route into Cphere, and no
 *     write action of any kind. The only outbound link is the wallet deep
 *     link the server supplies, when it supplies one.
 *
 * USD-denominated requests are paid at a server-issued quote that expires.
 * The page shows the quote's asset amount exactly as sent — that string is
 * what the payment URI / QR encodes — counts it down, and when it reaches
 * zero hides the amount and QR and re-fetches /status once. The server issues
 * the next quote on its own; the page only ever replaces the amount, unit
 * line, provenance, instructions and QR. The destination, reference and
 * network are the initial payload's for the life of the page.
 *
 * Cphere observes the destination address read-only. Nothing on this page can
 * initiate, sign or move funds.
 */

const API_BASE_URL = (
  process.env.REACT_APP_BACKEND_URL || "https://cphere-backend.onrender.com"
).replace(/\/+$/, "");

const PHASES = Object.freeze({
  LOADING: "loading",
  UNAVAILABLE: "unavailable",
  READY: "ready",
});

/** No credentials, no headers. The token in the path is the whole request. */
const PUBLIC_FETCH = Object.freeze({ cache: "no-store" });

const PAGE_TITLE = "Funding request · Cphere";
// Header + amount-row vocabulary is the server's (`requestTitle`, `amountLabel`);
// these are only the fallbacks for a payload issued before those fields.
const DEFAULT_REQUEST_TITLE = "Funding request";
const DEFAULT_AMOUNT_LABEL = "Requested compute amount";
const COMPUTE_SCOPE_COPY =
  "This request funds compute for the campaign. It is not the campaign's media investment.";
const UNAVAILABLE_COPY = "This funding request is not available.";
const READ_ONLY_COPY = "Cphere observes this address read-only and never signs or moves funds.";
const COPIED_RESET_MS = 1500;

// Same QR treatment Billing uses for its funding addresses: high error
// correction, a proper quiet zone, plain black on white so any wallet camera
// reads it. The value is the server's qrPayload and nothing else.
const QR_SIZE = 200;
const QR_QUIET_ZONE_MODULES = 2;

/**
 * Two scan targets, and the difference between them is money.
 *
 * PAYMENT — the server's `qrPayload`: destination, destination tag and amount
 *   in one payment-request URI, byte-identical to the Open Wallet link.
 * ADDRESS — the bare receiving address, for a wallet whose scan flow accepts an
 *   address and refuses a URI. It carries NO destination tag, so a payment sent
 *   from it alone cannot be attributed to this campaign. That is stated on the
 *   panel every time this mode is showing; it is never presented as a complete
 *   payment request.
 */
const QR_MODES = Object.freeze({ PAYMENT: "payment", ADDRESS: "address" });
const QR_MODE_LABELS = Object.freeze({
  [QR_MODES.PAYMENT]: "Scan payment request",
  [QR_MODES.ADDRESS]: "Scan address only",
});
const QR_PAYMENT_CAPTION = "Scan with your wallet — address, tag and amount included";
const QR_ADDRESS_CAPTION = "Address only — enter the tag and amount yourself";
const QR_ADDRESS_TAG_WARNING =
  "This code is the receiving address alone. Enter the destination tag and amount shown beside it — without the tag the payment cannot be attributed to this campaign.";

/**
 * Why a /status fetch was made. Interval and visibility polls chain ONE
 * immediate follow-up when the server answers without a usable quote; the
 * follow-ups themselves never chain, so an unavailable price costs at most
 * two requests per interval and can never loop.
 */
const POLL_REASONS = Object.freeze({
  INTERVAL: "interval",
  VISIBILITY: "visibility",
  QUOTE_EXPIRED: "quote_expired",
  QUOTE_MISSING: "quote_missing",
});

const EMPTY_VIEW = Object.freeze({ fundingRequest: null, quoteDeadlineMs: null });

/** One copy-to-clipboard button whose label flips to "Copied" briefly. */
function CopyButton({ value, label, copiedLabel = "Copied", className = "" }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  const onCopy = useCallback(async () => {
    const text = String(value ?? "");
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      }
    } catch {
      // Clipboard access can be refused; the value is still visible on the
      // page to select by hand, so a failed copy is not an error state.
    }
    setCopied(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);
  }, [value]);

  return (
    <button
      type="button"
      className={`pfr-copy-button${className ? ` ${className}` : ""}`}
      onClick={onCopy}
      disabled={!value}
      aria-live="polite"
      data-copied={copied ? "true" : "false"}
    >
      {copied ? copiedLabel : label}
    </button>
  );
}

/**
 * The asset's own mark, drawn beside the amount that is denominated in it.
 *
 * The artwork comes from the one funding-asset map the product already uses
 * for every rail (Treasury cards, Billing tiles, QR centre marks), resolved
 * from the STRUCTURED identity the server sent — asset code plus network —
 * so a mark can never be picked from a display label or guessed. An asset
 * that map does not carry draws nothing at all: the amount beside it already
 * names the asset, and a stand-in coin would be a claim about a network the
 * product does not serve. A USD figure never gets one either — this renders
 * only where the amount is the asset amount.
 *
 * Decorative by contract: the amount reads "185 XRP" on its own, so the mark
 * is hidden from assistive technology rather than announcing the asset twice,
 * and it contributes no text of its own to the line.
 */
function AssetMark({ asset, network, chainNetwork }) {
  const visual = resolveFundingAssetVisual({ asset, network: network || chainNetwork });
  if (!visual) return null;

  return (
    <span className="pfr-asset-mark" data-asset-visual={visual.key} aria-hidden="true">
      <img className="pfr-asset-mark-image" src={visual.src} alt="" draggable={false} />
    </span>
  );
}

/**
 * One asset amount: the mark, then the server's amount string untouched.
 *
 * The string sits in its own element so the line can be a flex row without
 * the amount's `overflow-wrap: anywhere` — which feeds min-content sizing —
 * collapsing it to one character per line beside the image.
 */
function AssetAmount({ children, asset, network, chainNetwork }) {
  return (
    <p className="pfr-amount-asset">
      <AssetMark asset={asset} network={network} chainNetwork={chainNetwork} />
      <span className="pfr-amount-asset-value">{children}</span>
    </p>
  );
}

export default function PublicFundingRequest() {
  const { fundingToken } = useParams();
  const token = String(fundingToken || "").trim();

  const [phase, setPhase] = useState(PHASES.LOADING);
  // The request plus the instant its current quote runs out (ms), anchored
  // to when the response carrying that quote arrived.
  const [view, setView] = useState(EMPTY_VIEW);
  const [nowMs, setNowMs] = useState(() => Date.now());
  // An advertiser mark the browser cannot draw is not a gap: the identity
  // block falls back to the advertiser's name for the life of the page.
  const [logoFailed, setLogoFailed] = useState(false);
  // Which scan target is on screen. Always opens on the complete payment
  // request; address-only is a deliberate step the payer takes.
  const [qrMode, setQrMode] = useState(QR_MODES.PAYMENT);

  const fundingRequest = view.fundingRequest;
  const status = fundingRequest?.status || "";
  const terminal = isTerminalFundingStatus(status);
  const usd = isUsdDenomination(fundingRequest);
  const quote = usd ? fundingRequest?.quote : null;
  const serverQuoteUsable = usd && quoteIsUsable(quote);
  const secondsLeft = serverQuoteUsable ? quoteSecondsLeft(view.quoteDeadlineMs, nowMs) : null;
  const quoteExpiredLocally = serverQuoteUsable && secondsLeft === 0;
  const quoteUsable = serverQuoteUsable && !quoteExpiredLocally;

  // Effects read the latest derived state through a ref so they can keep
  // their own (stable) dependency lists.
  const latestRef = useRef({ view, usd, serverQuoteUsable });
  latestRef.current = { view, usd, serverQuoteUsable };
  const pollRef = useRef(null);
  const expiryRefreshedForRef = useRef(null);

  // Document title + noindex for the life of the page only.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const previousTitle = document.title;
    document.title = PAGE_TITLE;
    let meta = null;
    try {
      meta = document.createElement("meta");
      meta.setAttribute("name", "robots");
      meta.setAttribute("content", "noindex");
      meta.setAttribute("data-public-funding-request", "true");
      document.head.appendChild(meta);
    } catch {
      meta = null;
    }
    return () => {
      document.title = previousTitle;
      if (meta && meta.parentNode) meta.parentNode.removeChild(meta);
    };
  }, []);

  // Initial load. Any failure — 404, network, malformed body — is the single
  // "not available" answer. The page never explains why.
  useEffect(() => {
    if (!token) {
      setPhase(PHASES.UNAVAILABLE);
      return undefined;
    }
    const controller = new AbortController();
    setPhase(PHASES.LOADING);
    setView(EMPTY_VIEW);

    fetch(buildPublicFundingBase(API_BASE_URL, token), {
      ...PUBLIC_FETCH,
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) throw new Error("unavailable");
        const body = await res.json();
        const next = body && body.ok ? sanitizePublicFundingRequest(body.fundingRequest) : null;
        if (!next) throw new Error("unavailable");
        if (controller.signal.aborted) return;
        const receivedAt = Date.now();
        setView({ fundingRequest: next, quoteDeadlineMs: quoteDeadlineMs(next.quote, receivedAt) });
        setNowMs(receivedAt);
        setPhase(PHASES.READY);
      })
      .catch(() => {
        if (!controller.signal.aborted) setPhase(PHASES.UNAVAILABLE);
      });

    return () => controller.abort();
  }, [token]);

  // Status polling: every 30s while the request is still open and the tab is
  // visible. Stops for good once a terminal status arrives. The same fetch
  // serves the quote refresh (see the expiry effect below).
  useEffect(() => {
    if (phase !== PHASES.READY || !token || terminal) return undefined;
    if (typeof document === "undefined" || typeof window === "undefined") return undefined;

    let cancelled = false;
    let inFlight = null;

    const poll = (reason = POLL_REASONS.INTERVAL) => {
      if (cancelled || inFlight) return;
      if (document.visibilityState === "hidden") return;
      let followUp = false;
      inFlight = fetch(`${buildPublicFundingBase(API_BASE_URL, token)}/status`, PUBLIC_FETCH)
        .then(async (res) => {
          if (!res.ok) return;
          const body = await res.json();
          if (cancelled || !body || !body.ok || !body.status) return;
          const current = latestRef.current.view;
          if (!current.fundingRequest) return;
          const receivedAt = Date.now();
          const merged = mergeFundingStatus(current.fundingRequest, body);
          const deadline =
            body.quote !== undefined ? quoteDeadlineMs(merged.quote, receivedAt) : current.quoteDeadlineMs;
          setView({ fundingRequest: merged, quoteDeadlineMs: deadline });
          setNowMs(receivedAt);
          followUp =
            isUsdDenomination(merged) &&
            !quoteIsUsable(merged.quote) &&
            !isTerminalFundingStatus(merged.status) &&
            (reason === POLL_REASONS.INTERVAL || reason === POLL_REASONS.VISIBILITY);
        })
        .catch(() => {})
        .finally(() => {
          inFlight = null;
        })
        .then(() => {
          if (followUp && !cancelled) poll(POLL_REASONS.QUOTE_MISSING);
        });
    };
    pollRef.current = poll;

    const interval = window.setInterval(() => poll(POLL_REASONS.INTERVAL), FUNDING_STATUS_POLL_INTERVAL_MS);
    // Coming back to the tab refreshes immediately rather than waiting out
    // the remainder of an interval that ran while hidden.
    const onVisibility = () => {
      if (document.visibilityState !== "hidden") poll(POLL_REASONS.VISIBILITY);
    };
    document.addEventListener("visibilitychange", onVisibility);

    // A USD request that loaded without a usable quote asks for one now.
    if (latestRef.current.usd && !latestRef.current.serverQuoteUsable) {
      poll(POLL_REASONS.QUOTE_MISSING);
    }

    return () => {
      cancelled = true;
      pollRef.current = null;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [phase, token, terminal]);

  // Quote clock: one tick a second while a USD request is open.
  useEffect(() => {
    if (phase !== PHASES.READY || terminal || !usd) return undefined;
    if (typeof window === "undefined") return undefined;
    const tick = window.setInterval(() => setNowMs(Date.now()), QUOTE_TICK_INTERVAL_MS);
    return () => window.clearInterval(tick);
  }, [phase, terminal, usd]);

  // Quote expiry: the countdown reaching zero re-fetches /status once per
  // deadline. The in-flight guard in poll() makes it a single request; the
  // ref makes it single per quote even though the clock keeps ticking.
  useEffect(() => {
    if (!quoteExpiredLocally || terminal) return;
    const key = view.quoteDeadlineMs;
    if (expiryRefreshedForRef.current === key) return;
    expiryRefreshedForRef.current = key;
    if (pollRef.current) pollRef.current(POLL_REASONS.QUOTE_EXPIRED);
  }, [quoteExpiredLocally, terminal, view.quoteDeadlineMs]);

  if (phase === PHASES.LOADING) {
    return (
      <div className="prx-page pfr-page" data-public-funding-request="loading">
        <div className="prx-unavailable" role="status" aria-busy="true">
          <p className="prx-unavailable-body">Loading funding request…</p>
        </div>
      </div>
    );
  }

  if (phase === PHASES.UNAVAILABLE || !fundingRequest) {
    return (
      <div className="prx-page pfr-page" data-public-funding-request="unavailable">
        <div className="prx-unavailable" role="status">
          <p className="prx-unavailable-title">{UNAVAILABLE_COPY}</p>
        </div>
      </div>
    );
  }

  const payment = fundingRequest.payment || {};
  const feePolicy = fundingRequest.feePolicy || {};
  // Instructions travel with the quote they describe. Once a quote has run
  // out on the page clock, its instruction lines (which name the amount) are
  // withheld until the refreshed response replaces them; a response that
  // carried no quote carried instructions written for no quote, so those
  // stay.
  const instructions =
    Array.isArray(fundingRequest.instructions) && !quoteExpiredLocally
      ? fundingRequest.instructions
      : [];
  const asset = fundingRequest.asset || payment.asset || "";
  const assetLabel = fundingRequest.assetLabel || "";
  const tokenStandard = fundingRequest.tokenStandard || "";
  const referenceLabel = payment.referenceLabel || "Reference";
  const referenceRequired = Boolean(payment.referenceRequired);
  const referenceValue = payment.referenceValue ?? "";
  const qrPayload = typeof payment.qrPayload === "string" ? payment.qrPayload.trim() : "";
  const addressQrPayload =
    typeof payment.addressQrPayload === "string" ? payment.addressQrPayload.trim() : "";
  const walletDeepLink =
    typeof payment.walletDeepLink === "string" ? payment.walletDeepLink.trim() : "";
  // The address-only code is only worth offering when it is a DIFFERENT scan
  // target from the payment request; on a rail whose payment payload already IS
  // the bare address there is nothing to choose between.
  const addressQrOffered = Boolean(addressQrPayload) && addressQrPayload !== qrPayload;
  const addressQrShowing = addressQrOffered && qrMode === QR_MODES.ADDRESS;
  const activeQrPayload = addressQrShowing ? addressQrPayload : qrPayload;
  // Native requests show and copy the fixed amount; USD requests the current
  // quote's asset amount, and only while that quote is current.
  const showAmount = !usd || quoteUsable;
  const paymentAmount = usd ? quote?.assetAmount ?? "" : payment.amount ?? fundingRequest.requestedAmount ?? "";
  const paymentAmountLine = usd ? formatQuoteAmount(quote, asset) : formatAssetAmount(paymentAmount, asset);
  const received = isNonZeroAmount(fundingRequest.receivedAmount);
  const overpaid = isNonZeroAmount(fundingRequest.overpaidAmount);
  const settledUsd = isNonZeroAmount(fundingRequest.settledUsd);
  const remainingUsd = settledUsd && isNonZeroAmount(fundingRequest.remainingUsd);
  const overpaymentUsd = isNonZeroAmount(fundingRequest.overpaymentUsd);
  const tone = statusTone(status);
  const issued = formatIssuedAt(fundingRequest.issuedAt);
  const expires = formatIssuedAt(fundingRequest.expiresAt);
  const networkLine = formatNetworkLine(fundingRequest.networkLabel, fundingRequest.chainNetwork);
  const assetLine = [assetLabel && assetLabel !== asset ? assetLabel : "", tokenStandard]
    .filter(Boolean)
    .join(" · ");
  const requestedUsdLine = [formatUsd(fundingRequest.requestedAmountUsd), fundingRequest.settlementCurrency]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");
  const payWithLine = formatPayWithLine(asset, fundingRequest.networkLabel, fundingRequest.chainNetwork);
  const requestTitle = String(fundingRequest.requestTitle || "").trim() || DEFAULT_REQUEST_TITLE;
  // Advertiser identity. The logo is a path under this page's own token, so
  // it is resolved against the API base exactly like every other public
  // asset; the server only sends one when a mark actually exists, and a mark
  // that fails to load falls back to the same name.
  const advertiserName = String(fundingRequest.advertiserName || "").trim();
  const advertiserLogoPath = String(fundingRequest.advertiserLogoUrl || "").trim();
  const advertiserLogoSrc =
    advertiserLogoPath && !logoFailed
      ? (/^https?:\/\//i.test(advertiserLogoPath) ? advertiserLogoPath : `${API_BASE_URL}${advertiserLogoPath}`)
      : "";
  const advertiserLogoAlt =
    String(fundingRequest.advertiserLogoAltText || "").trim() ||
    (advertiserName ? `${advertiserName} logo` : "Advertiser logo");
  const amountLabel = String(fundingRequest.amountLabel || "").trim() || DEFAULT_AMOUNT_LABEL;
  // The hero pill reads wordmark + capsule: everything but the last word,
  // then the last word.
  const titleBreak = requestTitle.lastIndexOf(" ");
  const titleWordmark = titleBreak > 0 ? requestTitle.slice(0, titleBreak) : requestTitle;
  const titleTag = titleBreak > 0 ? requestTitle.slice(titleBreak + 1) : "";
  const unitLine = quoteUsable ? quoteUnitLine(quote, asset) : "";
  const provenance = quoteUsable ? quoteProvenanceLine(quote, nowMs) : "";
  const quoteNotice = usd && !quoteUsable ? quoteUnavailableCopy(fundingRequest) : "";
  const countdownUrgent =
    quoteUsable &&
    !isStablecoinParQuote(quote) &&
    secondsLeft !== null &&
    secondsLeft <= QUOTE_URGENT_SECONDS;

  return (
    <div
      className="prx-page pfr-page"
      data-public-funding-request="true"
      data-status={status}
      data-denomination={usd ? "usd" : "native"}
    >
      <div className="cpx-page-container prx-page-container">
        <div className="cpx-page-content prx-page-content pfr-page-content">
          {/* Canonical brand mark, WITHOUT the authenticated onClick — there
              is no route from this page into the application. */}
          <CphereHeaderLogo className="cpx-brand-logo" />

          <header className="cphere-governed-hero-selector-stack prx-hero-stack">
            <div className="cpx-hero-container">
              <div
                className="cpx-hero-pill"
                role="heading"
                aria-level="1"
                aria-label={requestTitle}
              >
                <span className="cpx-hero-pill-label cphere-hero-pill-wordmark">{titleWordmark}</span>
                {titleTag ? (
                  <span className="cpx-hero-pill-tag cphere-hero-pill-tag">{titleTag}</span>
                ) : null}
              </div>
              <div className="cpx-hero-shimmer-bar" aria-hidden="true" />
            </div>

            {/* Identity, in the order a client reads it: whose campaign
                this is, then which campaign. The hero above already says
                what the page IS, so nothing here repeats it — and the
                person the link was sent to is not printed at all. */}
            <div className="prx-advertiser-stage pfr-identity">
              {advertiserLogoSrc ? (
                <img
                  className="prx-advertiser-logo pfr-advertiser-logo"
                  src={advertiserLogoSrc}
                  alt={advertiserLogoAlt}
                  loading="eager"
                  decoding="async"
                  onError={() => setLogoFailed(true)}
                />
              ) : advertiserName ? (
                /* No mark to carry the identity — the name carries it,
                   deliberately, rather than a broken image or a gap. */
                <p className="prx-advertiser-name pfr-advertiser-name">{advertiserName}</p>
              ) : null}
              {fundingRequest.campaignName ? (
                <h2 className="pfr-campaign-name">{fundingRequest.campaignName}</h2>
              ) : null}
            </div>
          </header>

          <main className="pfr-main">
            {/* ---------------- Amount + status ---------------- */}
            <section className="cpx-panel pfr-panel pfr-amount-panel" aria-label="Amount">
              {usd ? (
                <div className="pfr-quote-rows" data-quote={quoteUsable ? "active" : "pending"}>
                  <div className="pfr-quote-row">
                    <p className="pfr-label">{amountLabel}</p>
                    <p className="pfr-amount-usd">{requestedUsdLine}</p>
                  </div>
                  {payWithLine ? (
                    <div className="pfr-quote-row">
                      <p className="pfr-label">Pay with</p>
                      <p className="pfr-pay-with">{payWithLine}</p>
                    </div>
                  ) : null}
                  <div className="pfr-quote-row pfr-quote-row-amount">
                    <p className="pfr-label">Current quote</p>
                    {quoteUsable ? (
                      <div className="pfr-quote-amount">
                        {/* The server's assetAmount string, untouched: it is
                            what the URI / QR encode and what Copy copies. */}
                        <AssetAmount
                          asset={asset}
                          network={fundingRequest.network}
                          chainNetwork={fundingRequest.chainNetwork}
                        >
                          {paymentAmountLine}
                        </AssetAmount>
                        {unitLine ? <p className="pfr-quote-unit">{unitLine}</p> : null}
                      </div>
                    ) : (
                      <p className="pfr-quote-state" role="status" aria-live="polite">
                        {quoteNotice}
                      </p>
                    )}
                  </div>
                  {quoteUsable && secondsLeft !== null ? (
                    <div className="pfr-quote-row">
                      <p className="pfr-label">Quote valid for</p>
                      <p
                        className="pfr-quote-countdown"
                        data-urgent={countdownUrgent ? "true" : "false"}
                        aria-live="off"
                      >
                        {formatCountdown(secondsLeft)}
                      </p>
                    </div>
                  ) : null}
                  {provenance ? <p className="pfr-quote-provenance">{provenance}</p> : null}
                </div>
              ) : (
                <div className="pfr-amount-grid">
                  <div className="pfr-amount-block">
                    <p className="pfr-label">{amountLabel}</p>
                    <p className="pfr-amount-usd">{formatUsd(fundingRequest.requestedAmountUsd)}</p>
                    {fundingRequest.settlementCurrency ? (
                      <p className="pfr-muted">Settled in {fundingRequest.settlementCurrency}</p>
                    ) : null}
                  </div>
                  <div className="pfr-amount-block">
                    <p className="pfr-label">Payment amount</p>
                    <AssetAmount
                      asset={asset}
                      network={fundingRequest.network}
                      chainNetwork={fundingRequest.chainNetwork}
                    >
                      {formatAssetAmount(fundingRequest.requestedAmount, asset)}
                    </AssetAmount>
                    {networkLine ? <p className="pfr-network">{networkLine}</p> : null}
                    {assetLine ? <p className="pfr-muted">{assetLine}</p> : null}
                  </div>
                </div>
              )}

              <p className="pfr-muted pfr-compute-scope">{COMPUTE_SCOPE_COPY}</p>

              <div className="pfr-status-row">
                <span className="pfr-status-pill" data-tone={tone} data-status={status}>
                  {fundingRequest.statusLabel || status}
                </span>
                {usd ? (
                  <>
                    {settledUsd ? (
                      <span className="pfr-status-detail pfr-settled-usd">
                        Confirmed value {formatUsd(fundingRequest.settledUsd)}
                      </span>
                    ) : null}
                    {remainingUsd ? (
                      <span className="pfr-status-detail pfr-remaining-usd">
                        Remaining {formatUsd(fundingRequest.remainingUsd)}
                      </span>
                    ) : null}
                    {overpaymentUsd ? (
                      <span className="pfr-status-detail pfr-overpaid pfr-overpayment-usd">
                        Overpayment {formatUsd(fundingRequest.overpaymentUsd)}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <>
                    {received ? (
                      <span className="pfr-status-detail pfr-received">
                        Received {formatAssetAmount(fundingRequest.receivedAmount)} /{" "}
                        {formatAssetAmount(fundingRequest.requestedAmount, asset)}
                        {fundingRequest.remainingAmount != null &&
                        isNonZeroAmount(fundingRequest.remainingAmount) ? (
                          <>
                            {" · "}Remaining{" "}
                            {formatAssetAmount(fundingRequest.remainingAmount, asset)}
                          </>
                        ) : null}
                      </span>
                    ) : null}
                    {overpaid ? (
                      <span className="pfr-status-detail pfr-overpaid">
                        Overpaid by {formatAssetAmount(fundingRequest.overpaidAmount, asset)}
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            </section>

            <div className="pfr-payment-grid">
              {/* ---------------- QR ---------------- */}
              <section
                className="cpx-panel pfr-panel pfr-qr-panel"
                aria-label="Payment QR code"
                data-qr-mode={addressQrShowing ? QR_MODES.ADDRESS : QR_MODES.PAYMENT}
              >
                {/* The address-only code is a second scan target, never a
                    replacement: an address without its destination tag cannot be
                    attributed, so the two are named apart and the tagless one
                    says so while it is showing. */}
                {addressQrOffered ? (
                  <div className="pfr-qr-modes" role="group" aria-label="Choose what the code contains">
                    {[QR_MODES.PAYMENT, QR_MODES.ADDRESS].map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        className="pfr-qr-mode"
                        data-mode={mode}
                        aria-pressed={
                          (mode === QR_MODES.ADDRESS) === addressQrShowing ? "true" : "false"
                        }
                        onClick={() => setQrMode(mode)}
                      >
                        {QR_MODE_LABELS[mode]}
                      </button>
                    ))}
                  </div>
                ) : null}

                {!showAmount && !addressQrShowing ? (
                  <div className="pfr-qr-wrap pfr-qr-unavailable" data-qr="awaiting-quote">
                    <p className="pfr-qr-unavailable-copy">{QUOTE_AWAITING_QR_COPY}</p>
                  </div>
                ) : activeQrPayload ? (
                  <div className="pfr-qr-wrap" data-qr="present">
                    <QRCodeSVG
                      value={activeQrPayload}
                      title={
                        addressQrShowing
                          ? "Receiving address QR code"
                          : "Funding payment request QR code"
                      }
                      className="pfr-qr-image"
                      size={QR_SIZE}
                      level="H"
                      marginSize={QR_QUIET_ZONE_MODULES}
                      bgColor="#ffffff"
                      fgColor="#000000"
                    />
                  </div>
                ) : (
                  <div className="pfr-qr-wrap pfr-qr-unavailable" data-qr="unavailable">
                    <p className="pfr-qr-unavailable-copy">
                      QR unavailable — use the address and reference below
                    </p>
                  </div>
                )}

                <p className="pfr-qr-caption">
                  {addressQrShowing ? QR_ADDRESS_CAPTION : QR_PAYMENT_CAPTION}
                </p>

                {addressQrShowing ? (
                  <p className="pfr-qr-tag-warning" role="note">
                    {QR_ADDRESS_TAG_WARNING}
                  </p>
                ) : null}

                {/* Same string as the QR, by construction — the server sends one
                    payment contract and both surfaces read it. */}
                {!addressQrShowing && showAmount && walletDeepLink ? (
                  <a className="pfr-wallet-link" href={walletDeepLink} rel="noopener">
                    Open wallet
                  </a>
                ) : null}
              </section>

              {/* ---------------- Payment details ---------------- */}
              <section className="cpx-panel pfr-panel pfr-details-panel" aria-label="Payment details">
                <dl className="pfr-details">
                  <div className="pfr-detail-row">
                    <dt className="pfr-label">Address</dt>
                    <dd className="pfr-detail-value pfr-address">
                      <code>{payment.address || "—"}</code>
                    </dd>
                    <CopyButton value={payment.address} label="Copy address" />
                  </div>

                  {referenceRequired ? (
                    <div className="pfr-detail-row" data-reference-required="true">
                      <dt className="pfr-label">{referenceLabel}</dt>
                      <dd className="pfr-detail-value pfr-reference">
                        <code>{String(referenceValue || "—")}</code>
                      </dd>
                      <CopyButton value={referenceValue} label="Copy tag/memo" />
                    </div>
                  ) : null}

                  {showAmount ? (
                    <div className="pfr-detail-row" data-amount-row="true">
                      <dt className="pfr-label">Amount</dt>
                      <dd className="pfr-detail-value pfr-amount">
                        <code>{paymentAmountLine}</code>
                      </dd>
                      <CopyButton value={paymentAmount} label="Copy amount" />
                    </div>
                  ) : null}
                </dl>
              </section>
            </div>

            {/* ---------------- Instructions ---------------- */}
            <section className="cpx-panel pfr-panel pfr-instructions-panel" aria-label="Instructions">
              <h3 className="cpx-panel-title pfr-panel-title">How to pay</h3>
              {instructions.length ? (
                <ol className="pfr-instructions">
                  {instructions.map((line, index) => (
                    // Server copy, rendered verbatim and in order.
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={index}>{line}</li>
                  ))}
                </ol>
              ) : null}
              {referenceRequired ? (
                <p className="pfr-reference-warning" role="alert">
                  Include the {referenceLabel} — without it the payment cannot be attributed to
                  this campaign.
                </p>
              ) : null}
              <ul className="pfr-fees" aria-label="Fees">
                {feePolicy.cphereFeeLabel ? <li>{feePolicy.cphereFeeLabel}</li> : null}
                {feePolicy.networkFeeLabel ? <li>{feePolicy.networkFeeLabel}</li> : null}
              </ul>
            </section>

            {/* ---------------- Safety ---------------- */}
            <section className="pfr-safety" aria-label="Safety">
              {fundingRequest.safetyCopy ? (
                <p className="pfr-safety-copy">{fundingRequest.safetyCopy}</p>
              ) : null}
              <p className="pfr-safety-copy">{READ_ONLY_COPY}</p>
            </section>
          </main>

          <CphereBrandFooter
            className="prx-footer pfr-footer"
            trail=""
            textClassName="prx-footer-credit"
            note={
              <p className="prx-footer-note pfr-footer-note">
                {issued ? <span>Issued {issued}</span> : null}
                {issued && expires ? " · " : null}
                {expires ? <span>Link expires {expires}</span> : null}
              </p>
            }
          />
        </div>
      </div>
    </div>
  );
}
